export interface DocPage {
  title: string;
  description: string;
  slug: string;
}

export interface NavSection {
  label: string;
  pages: DocPage[];
}

export const navigation: NavSection[] = [
  {
    label: "Get Started",
    pages: [
      {
        title: "Introduction",
        description:
          "What Blucor does and how the platform works for your hiring team.",
        slug: "introduction",
      },
      {
        title: "Platform Overview",
        description:
          "Key concepts and how the main sections of the platform work together.",
        slug: "platform-overview",
      },
      {
        title: "Onboarding Wizard",
        description:
          "The five-step setup a new account walks through before landing in the app.",
        slug: "onboarding-wizard",
      },
      {
        title: "Quick Start Guide",
        description:
          "Step-by-step: From first login to your first AI-screened candidate.",
        slug: "quick-start-guide",
      },
      {
        title: "Asking Your AI Assistant",
        description:
          "Use the Ask panel in the sidebar to get how-to answers instead of searching the Help Center yourself.",
        slug: "asking-your-ai-assistant",
      },
    ],
  },
  {
    label: "Jobs",
    pages: [
      {
        title: "Creating a Job with AI",
        description:
          "Create a new position by talking to your AI assistant instead of filling out a form.",
        slug: "creating-a-job-with-ai",
      },
      {
        title: "Creating a Job from Your ATS",
        description:
          "How to import a position from your ATS and set up AI screening.",
        slug: "creating-a-job-ats",
      },
      {
        title: "Creating a Job Manually",
        description:
          "Upload a job description file, paste a posting link, or write the position yourself.",
        slug: "creating-a-job-manual",
      },
      {
        title: "Managing Jobs",
        description:
          "Search, filter, and bulk-manage your positions, including archiving, unarchiving, and copying a job's public link.",
        slug: "managing-jobs",
      },
    ],
  },
  {
    label: "AI Agent Setup",
    pages: [
      {
        title: "Setup Overview & Position Details",
        description:
          "The four setup groups, the five-row (max) setup progress meter, Job details, and Publish/Update.",
        slug: "ai-agent-setup",
      },
      {
        title: "Job Board Ads",
        description:
          "Choose a budget, review the AI-recommended ad plan and applicant estimate, and open checkout.",
        slug: "job-board-ads",
      },
      {
        title: "Resume screening",
        description: "Knockout factors and Copy from other positions.",
        slug: "setup-qualifications",
      },
      {
        title: "Phone interview",
        description:
          "Required and Preferred qualifications, Custom questions, Follow-ups, and the estimated call length.",
        slug: "setup-additional-questions",
      },
      {
        title: "Candidate FAQ",
        description:
          "Set the answers AI gives candidates about pay, schedule, and benefits during the call.",
        slug: "setup-candidate-faq",
      },
      {
        title: "Auto invite rules",
        description:
          "Target Invitees, the invitation text message, and the sending schedule or instant invites.",
        slug: "setup-ai-phone-call-invite",
      },
      {
        title: "Who gets reports",
        description:
          "Who gets emailed after a phone interview, attaching resumes, and copying recipients from another position.",
        slug: "setup-report-recipients",
      },
      {
        title: "Position Access & Members",
        description:
          "Assign or remove current members' access to a position, and preassign access for a pending invite.",
        slug: "setup-position-access",
      },
    ],
  },
  {
    label: "Simulation & Test Calls",
    pages: [
      {
        title: "Running a Simulation",
        description:
          "Have AI run through the entire interview flow on its own, so you can review the question sequence before candidates start calling in.",
        slug: "running-a-simulation",
      },
      {
        title: "Reviewing Simulation Results",
        description:
          "Read the category-by-category simulation transcript, approve or ignore each suggested fix, and apply the changes to your position.",
        slug: "reviewing-simulation-results",
      },
      {
        title: "Test Call",
        description:
          "Experience the AI phone interview as a candidate before going live.",
        slug: "test-call",
      },
    ],
  },
  {
    label: "Candidates",
    pages: [
      {
        title: "Candidate Lists by Job",
        description:
          "The Screened, Interviewed, and Scheduling tabs on a job's own candidate list — how to get there, what each shows, and how the export works.",
        slug: "candidate-lists-by-job",
      },
      {
        title: "Adding Applicants",
        description:
          "Upload resumes by hand from the Screened tab, what upload errors mean, and how the ATS Source Filter relates to manual uploads.",
        slug: "adding-applicants",
      },
      {
        title: "AI Assessment & Invite Statuses",
        description:
          "What the AI assessment and AI invite columns mean, including the automated pipeline's Preparing, Blocked, Invited, and Not auto-invited states.",
        slug: "ai-assessment-invite-statuses",
      },
      {
        title: "Inviting Candidates to AI Calls",
        description:
          "Send a manual AI call invite to one or several Screened candidates, and why some selections don't go through.",
        slug: "inviting-candidates",
      },
      {
        title: "Editing & Archiving Candidates",
        description:
          "Correct a candidate's name or phone number, and archive or unarchive a candidate without deleting their record.",
        slug: "editing-archiving-candidates",
      },
      {
        title: "Reassessing Candidates",
        description:
          "Re-run the AI evaluation for already-interviewed candidates after you change a job's qualifications or questions.",
        slug: "reassessing-candidates",
      },
      {
        title: "Searching All Candidates",
        description:
          "How to find candidates across your talent pool by name, and how the company-wide export differs from a job's own.",
        slug: "searching-candidates",
      },
      {
        title: "Resume Review",
        description:
          "The AI Review and Resume tabs on a candidate's resume record, including the qualification table and file attachments.",
        slug: "resume-review",
      },
    ],
  },
  {
    label: "Interview Results & Decisions",
    pages: [
      {
        title: "Phone Interview Results",
        description:
          "Review the six evaluation tabs, share results by email, ask AI about the interview, and move to a rejection decision.",
        slug: "phone-interview-results",
      },
      {
        title: "Call History & Recordings",
        description:
          "Listen to the interview recording, read the transcript, click a line to jump the audio there, and see why a call disconnected.",
        slug: "call-history-recordings",
      },
      {
        title: "Asking AI About a Candidate",
        description:
          "Ask your AI assistant follow-up questions about a candidate's evaluation, right from the results page.",
        slug: "asking-ai-about-a-candidate",
      },
      {
        title: "Sharing Candidate Results",
        description:
          "Email a candidate's results to your hiring team, copy recipients from another position, attach the resume, and add a note.",
        slug: "sharing-candidate-results",
      },
      {
        title: "Accepting & Rejecting Candidates",
        description:
          "Reject a candidate, leave feedback that changes what AI asks next time, and send the right rejection notice for your ATS setup.",
        slug: "accepting-rejecting-candidates",
      },
    ],
  },
  {
    label: "Communication",
    pages: [
      {
        title: "Chat History",
        description:
          "View past text conversations between a candidate and your AI assistant or your team, with an on-demand translation toggle.",
        slug: "chat-history",
      },
      {
        title: "Manual Messaging",
        description:
          "Send a personal text message to a candidate outside the automated flow.",
        slug: "manual-messaging",
      },
      {
        title: "Scheduling Calls & Call Now",
        description:
          "Schedule a candidate's AI phone interview for a specific date and time, or call them immediately.",
        slug: "scheduling-calls-call-now",
      },
    ],
  },
  {
    label: "Action Items",
    pages: [
      {
        title: "Action Items Overview",
        description:
          "See what needs your decision across In Process, Offer, Unreachable, Handoffs, Follow-ups, and FAQs, and resolve items one by one, or in bulk on the tabs that support it.",
        slug: "managing-your-to-dos",
      },
      {
        title: "In Process",
        description:
          "Schedule human interviews with a text thread and, after screening, an email-to-text draft.",
        slug: "action-items-human-interviews",
      },
      {
        title: "Unreachable Candidates",
        description:
          "Fix an undeliverable phone number so your AI assistant can reach a candidate who never got the invite.",
        slug: "action-items-unreachable",
      },
      {
        title: "Handoffs & Follow-ups",
        description:
          "Respond to candidates who asked for a recruiter or are checking on their application status.",
        slug: "action-items-handoffs-followups",
      },
      {
        title: "Answering Candidate FAQs",
        description:
          "Answer the questions your AI assistant couldn't during a screening call, and save them to a Position or Company FAQ.",
        slug: "action-items-faqs",
      },
    ],
  },
  {
    label: "Automations",
    pages: [
      {
        title: "Automations Overview",
        description:
          "The four automation cards you'll see, why ATS Pipeline Sync and Rejection Message never show at the same time, and what Report Email Template and FAQ Library configure.",
        slug: "automations-overview",
      },
      {
        title: "AI Interview Invite",
        description:
          "The organization-wide default for who gets invited, the invite text, and the sending schedule.",
        slug: "ai-interview-invite",
      },
      {
        title: "ATS Pipeline Sync",
        description: "Automate candidate status updates in your ATS.",
        slug: "ats-pipeline-settings",
      },
      {
        title: "Rejection Message",
        description:
          "Customize the text sent to rejected candidates when your organization has no ATS connected.",
        slug: "rejection-message",
      },
      {
        title: "FAQ Library",
        description:
          "Manage the company-wide and position-specific answers your AI assistant gives to candidate questions.",
        slug: "faq-library",
      },
      {
        title: "Keeping FAQs Answered",
        description:
          "Understand needs-answer grouping, blank vs. unanswered FAQs, and how a question moves into your Company FAQ.",
        slug: "keeping-faqs-answered",
      },
    ],
  },
  {
    label: "AI Performance",
    pages: [
      {
        title: "AI Performance Overview",
        description: "Understanding your metrics and what each number means.",
        slug: "dashboard-overview",
      },
      {
        title: "Dashboard Settings & Offer Stages",
        description:
          "Who can save the baseline hiring fields, and why Offer Stages needs an ATS connection and Admin access.",
        slug: "dashboard-settings-offer-stages",
      },
      {
        title: "Exporting Reports",
        description: "Download your dashboard as a PDF or a CSV.",
        slug: "exporting-reports",
      },
    ],
  },
  {
    label: "Settings & Workspace",
    pages: [
      {
        title: "Team Members",
        description: "Invite colleagues and manage roles and permissions.",
        slug: "team-members",
      },
      {
        title: "Company Info & Public Jobs Page",
        description:
          "Edit your company profile, manage multiple companies, and publish a public jobs page.",
        slug: "company-info-public-jobs",
      },
      {
        title: "Plans & Billing",
        description: "View your current plan, usage, and billing details.",
        slug: "plans-and-billing",
      },
      {
        title: "ATS Integration",
        description:
          "Connect Blucor with your existing Applicant Tracking System.",
        slug: "ats-integration",
      },
      {
        title: "Email Notifications",
        description:
          "Configure who receives the daily and weekly hiring summary emails.",
        slug: "reports-and-notifications",
      },
      {
        title: "API Access & Data Scope",
        description:
          "How far an ATS connection's access reaches, and how to limit or work without one.",
        slug: "api-access-data-scope",
      },
    ],
  },
  {
    label: "Retention Support",
    pages: [
      {
        title: "Post-Hire Check-Ins",
        description: "AI-powered retention support for new employees.",
        slug: "post-hire-check-ins",
      },
    ],
  },
  {
    label: "Troubleshooting & FAQ",
    pages: [
      {
        title: "Candidates Aren't Getting Invites or Calls",
        description:
          "Candidate applied but never got a text, got the text but no call came, or a landline or blocked number is stopping delivery.",
        slug: "troubleshooting-invites",
      },
      {
        title: "Positions Aren't Syncing From Your ATS",
        description:
          "A new position isn't showing up from your ATS, or you want to start a sync now instead of waiting for the next automatic refresh.",
        slug: "troubleshooting-ats-sync",
      },
      {
        title: "Working Without or Alongside Your ATS",
        description:
          "Use Blucor without an ATS connection, screen only new applicants going forward, or limit what Blucor can access from your ATS.",
        slug: "troubleshooting-without-ats",
      },
    ],
  },
];

export function findPageBySlug(slug: string): DocPage | undefined {
  for (const section of navigation) {
    for (const page of section.pages) {
      if (page.slug === slug) return page;
    }
  }
  return undefined;
}
